import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonText,
  useIonToast,
} from "@ionic/react";
import {
  createOutline,
  downloadOutline,
  informationCircleOutline,
} from "ionicons/icons";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import Header from "src/components/Header";
import { EvaluacionResumen } from "src/components/SolicitudRRHHEvaluacion";
import {
  SolicitudDetalle,
  colorEstado,
  etiquetaEstado,
  obtenerSolicitud,
} from "src/services/solicitudesRRHH";

const Fila: React.FC<{ etiqueta: string; valor: string }> = ({ etiqueta, valor }) => {
  if (!valor) return null;
  return (
    <IonItem lines="none">
      <IonLabel className="ion-text-wrap">
        <IonNote>{etiqueta}</IonNote>
        <p style={{ color: "var(--ion-text-color)", whiteSpace: "pre-line" }}>{valor}</p>
      </IonLabel>
    </IonItem>
  );
};

const SolicitudRRHHDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [present] = useIonToast();
  const [solicitud, setSolicitud] = useState<SolicitudDetalle | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setSolicitud(await obtenerSolicitud(Number(id)));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "No se pudo cargar la solicitud";
      present({ message: msg, duration: 3500, color: "danger" });
      history.replace("/solicitudes-rrhh");
    } finally {
      setCargando(false);
    }
  }, [id, present, history]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  /**
   * Abre un adjunto. La URL firmada que trajo el detalle vence en minutos, asi
   * que se pide el detalle de nuevo para usar una recien emitida: si el usuario
   * dejo la pantalla abierta, la que tenia en memoria ya no sirve.
   */
  const abrirAdjunto = useCallback(
    async (adjuntoId: number) => {
      try {
        const fresca = await obtenerSolicitud(Number(id));
        setSolicitud(fresca);
        const url = fresca.adjuntos.find((a) => a.id === adjuntoId)?.url_firmada;
        if (!url) {
          present({
            message: "El archivo no esta disponible para descargar",
            duration: 3500,
            color: "warning",
          });
          return;
        }
        window.open(url, "_blank");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "No se pudo abrir el archivo";
        present({ message: msg, duration: 3500, color: "danger" });
      }
    },
    [id, present]
  );

  return (
    <IonPage>
      <Header titulo={`Solicitud #${id}`} />
      <IonContent>
        <IonLoading isOpen={cargando} message="Cargando..." />

        {solicitud && (
          <>
            <IonCard>
              <IonCardContent>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "0.5rem",
                  }}
                >
                  <h2 style={{ margin: 0 }}>{solicitud.motivo_texto}</h2>
                  <IonBadge color={colorEstado(solicitud.estado)}>
                    {etiquetaEstado(solicitud.estado)}
                  </IonBadge>
                </div>
                <IonList lines="none">
                  <Fila etiqueta="Estado" valor={solicitud.estado_texto} />
                  <Fila etiqueta="Fecha de registro" valor={solicitud.fecha_registro} />
                  <Fila etiqueta="Solicitante" valor={solicitud.solicitante} />
                </IonList>

                {solicitud.estado === "PENDIENTE_JEFE" && (
                  <IonText color="warning">
                    <p style={{ marginTop: "0.5rem" }}>
                      <IonIcon
                        icon={informationCircleOutline}
                        style={{ verticalAlign: "middle", marginRight: 6 }}
                      />
                      Esperando la aprobacion de{" "}
                      {solicitud.jefe || "el jefe directo"}. La aprobacion se hace
                      desde EMSYS.
                    </p>
                  </IonText>
                )}
              </IonCardContent>
            </IonCard>

            {(solicitud.colaborador_nombre || solicitud.colaborador_legajo) && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Colaborador</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    <Fila etiqueta="Legajo" valor={solicitud.colaborador_legajo} />
                    <Fila etiqueta="Nombre" valor={solicitud.colaborador_nombre} />
                    <Fila etiqueta="Cedula" valor={solicitud.colaborador_cedula} />
                    <Fila etiqueta="Ciudad" valor={solicitud.colaborador_ciudad} />
                    <Fila etiqueta="Direccion" valor={solicitud.colaborador_direccion} />
                    <Fila etiqueta="Telefono" valor={solicitud.colaborador_telefono} />
                    <Fila
                      etiqueta="Fecha de ingreso"
                      valor={solicitud.colaborador_fecha_ingreso}
                    />
                    <Fila etiqueta="Punto de servicio" valor={solicitud.punto_servicio} />
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {solicitud.campos.length > 0 && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Datos de la solicitud</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    {solicitud.campos.map((c) => (
                      <Fila key={c.etiqueta} etiqueta={c.etiqueta} valor={c.valor} />
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {(solicitud.descuentos || []).length > 0 && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>
                    Colaboradores a descontar ({solicitud.descuentos.length})
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  {/* Una tarjeta por colaborador, igual que en la carga: en un
                      telefono una tabla de 7 columnas no se puede leer. */}
                  <IonList lines="full">
                    {solicitud.descuentos.map((d) => (
                      <IonItem key={d.orden} lines="full">
                        <IonLabel className="ion-text-wrap">
                          <h3 style={{ fontWeight: 600 }}>
                            {d.nombre || d.legajo || "(sin datos)"}
                          </h3>
                          <p>
                            <IonNote color="medium">Legajo </IonNote>
                            {d.legajo || "-"}
                            <IonNote color="medium"> · CI </IonNote>
                            {d.cedula || "-"}
                          </p>
                          <p>
                            <IonNote color="medium">Empresa </IonNote>
                            {d.empresa || "-"}
                            <IonNote color="medium"> · Unidad </IonNote>
                            {d.unidad_negocio || "-"}
                          </p>
                          <p>
                            <IonNote color="medium">Cuotas </IonNote>
                            {d.cantidad_cuotas || "-"}
                          </p>
                          {/* Factura e ID los completan Finanzas y RRHH: hasta
                              que pasen por sus bandejas no hay nada que
                              mostrar, y mostrar un guion confunde. */}
                          {(d.numero_factura || d.id_descuento) && (
                            <p>
                              <IonNote color="medium">Factura </IonNote>
                              {d.numero_factura || "-"}
                              <IonNote color="medium"> · ID </IonNote>
                              {d.id_descuento || "-"}
                            </p>
                          )}
                        </IonLabel>
                        <IonNote slot="end" color="dark">
                          {d.importe}
                        </IonNote>
                      </IonItem>
                    ))}
                  </IonList>
                  {solicitud.descuento_total && (
                    <IonItem lines="none">
                      <IonLabel>
                        <strong>Total a descontar</strong>
                      </IonLabel>
                      <IonNote slot="end" color="primary">
                        <strong>{solicitud.descuento_total}</strong>
                      </IonNote>
                    </IonItem>
                  )}
                </IonCardContent>
              </IonCard>
            )}

            {solicitud.evaluacion && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Evaluación de desempeño</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <EvaluacionResumen evaluacion={solicitud.evaluacion} />
                </IonCardContent>
              </IonCard>
            )}

            {solicitud.adjuntos.length > 0 && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Adjuntos</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    {solicitud.adjuntos.map((a) => (
                      <IonItem
                        key={a.id}
                        lines="none"
                        button
                        detail={false}
                        onClick={() => void abrirAdjunto(a.id)}
                      >
                        <IonIcon icon={downloadOutline} slot="start" />
                        <IonLabel className="ion-text-wrap">
                          <IonNote>{a.etiqueta_campo}</IonNote>
                          <p style={{ color: "var(--ion-text-color)" }}>
                            {a.nombre_archivo}
                          </p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {solicitud.comentario_devolucion && (
              <IonCard color="warning">
                <IonCardHeader>
                  <IonCardSubtitle>
                    {solicitud.devuelta_por || "Devuelta"} la devolvio para
                    corregir
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <p style={{ margin: 0 }}>{solicitud.comentario_devolucion}</p>
                  {/* Solo al duenio de la solicitud: lo decide EMSYS. */}
                  {solicitud.puede_reenviar && (
                    <IonButton
                      expand="block"
                      style={{ marginTop: 12 }}
                      routerLink={`/solicitudes-rrhh/${solicitud.id}/corregir`}
                    >
                      <IonIcon icon={createOutline} slot="start" />
                      Corregir y reenviar
                    </IonButton>
                  )}
                </IonCardContent>
              </IonCard>
            )}

            {(solicitud.comentario_finanzas ||
              solicitud.resuelto_por_finanzas) && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Verificacion de Finanzas</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    <Fila
                      etiqueta="Verificado por"
                      valor={solicitud.resuelto_por_finanzas}
                    />
                    <Fila
                      etiqueta="Fecha"
                      valor={solicitud.fecha_resolucion_finanzas}
                    />
                    <Fila
                      etiqueta="Comentario"
                      valor={solicitud.comentario_finanzas}
                    />
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {(solicitud.comentario_jefe || solicitud.resuelto_por_jefe) && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Aprobacion del jefe</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    <Fila etiqueta="Resuelto por" valor={solicitud.resuelto_por_jefe} />
                    <Fila etiqueta="Fecha" valor={solicitud.fecha_resolucion_jefe} />
                    <Fila etiqueta="Comentario" valor={solicitud.comentario_jefe} />
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {(solicitud.comentario_rrhh || solicitud.resuelto_por_rrhh) && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Gestion de RRHH</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    <Fila etiqueta="Resuelto por" valor={solicitud.resuelto_por_rrhh} />
                    <Fila etiqueta="Fecha" valor={solicitud.fecha_resolucion_rrhh} />
                    <Fila etiqueta="Comentario" valor={solicitud.comentario_rrhh} />
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}

            {solicitud.eventos.length > 0 && (
              <IonCard>
                <IonCardHeader>
                  <IonCardSubtitle>Historial</IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList lines="none">
                    {solicitud.eventos.map((e, idx) => (
                      <IonItem key={idx} lines="none">
                        <IonLabel className="ion-text-wrap">
                          <IonNote>{e.timestamp}</IonNote>
                          <p style={{ color: "var(--ion-text-color)" }}>
                            {e.tipo_texto}
                            {e.autor ? ` - ${e.autor}` : ""}
                          </p>
                          {e.detalle && <IonNote>{e.detalle}</IonNote>}
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </IonCardContent>
              </IonCard>
            )}
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SolicitudRRHHDetalle;
